import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Flame, ArrowRight, Check, X, Upload, Palette, Download, Shield, Zap,
  Sparkles, Layers, Wand2, ShirtIcon, Cpu, Star, Quote, Eraser, Camera,
  PlayCircle, Award, Users, TrendingUp, Rocket, Clock, ShieldCheck,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CompareSlider from "@/components/CompareSlider";
import TryItWidget from "@/components/TryItWidget";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

/* ---------------- Data ---------------- */

const MARQUEE_ITEMS = [
  "12 Halftone Styles", "AI Vector Cleanup", "Smart Underbase", "True AM Halftone",
  "300 DPI Press-Ready", "DTF + DTG", "Fabric Picker", "Beginner-Friendly",
  "Browser-Based", "Softer Hand", "Lifetime Access", "No Photoshop Needed",
];

const HALFTONE_STYLES_PREVIEW = [
  { name: "AM Round", angle: "22.5°" },
  { name: "Square", angle: "0°" },
  { name: "Diamond", angle: "45°" },
  { name: "Ellipse", angle: "30°" },
  { name: "Line H", angle: "0°" },
  { name: "Line 45°", angle: "45°" },
  { name: "Cross", angle: "0°" },
  { name: "Hex", angle: "30°" },
  { name: "Concentric", angle: "—" },
  { name: "Wave", angle: "0°" },
  { name: "FM Stochastic", angle: "—" },
  { name: "Newsprint", angle: "22.5°" },
];

const TESTIMONIALS = [
  {
    name: "Ed — Edwana Creations",
    location: "Yardley, PA",
    quote: "Soft, breathable prints that hold up wash after wash. The hand feel is night and day, and customers keep coming back for reorders.",
    img: "https://images.pexels.com/photos/6620958/pexels-photo-6620958.jpeg",
  },
  {
    name: "Marcy — Next Step Apparel",
    location: "Wilmington, DE",
    quote: "It nails the white underbase automatically. No flooded panels, no stiff prints. Prepress that used to eat 30 minutes now takes about one.",
    img: "https://images.unsplash.com/photo-1580489944761-15a19d654956",
  },
  {
    name: "Jordan — Studio 6ix",
    location: "Austin, TX",
    quote: "The AI vector creator is a cheat code. Bad phone photo in, print-ready graphic out. It changed how we quote small jobs.",
    img: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e",
  },
  {
    name: "Rae — Loom & Ink",
    location: "Portland, OR",
    quote: "Softer prints, better margins. Our reprint rate dropped by half in the first month. Cannot go back to Photoshop halftones.",
    img: "https://images.unsplash.com/photo-1544005313-94ddf0286df2",
  },
];

const FAQS = [
  {
    q: "Does Furnace Design Lab replace my RIP?",
    a: "No. We create the press-ready separation files (color + white underbase). You still push them through your RIP or DTF workflow. Think of us as the prepress layer that saves you 30 minutes a job.",
  },
  {
    q: "Do I need Photoshop?",
    a: "Nope. Everything runs in your browser — Mac, Windows, Chromebook. Upload PNG, JPG, or SVG, pick a fabric type and a halftone style, and download your files.",
  },
  {
    q: "What file formats can I upload?",
    a: "PNG (transparent recommended), JPG, and SVG. For DTF a transparent PNG at any resolution works — we resample to 300 DPI on export.",
  },
  {
    q: "How does the AI Vector Creator work?",
    a: "Type a prompt or upload a rough photo. Our AI (Gemini Nano Banana + gpt-image-1) cleans edges, kills muddy gradients, punches saturation, and outputs a print-ready graphic on a transparent background. Creator and All-Access users get automatic DTF/DTG optimization on every image.",
  },
  {
    q: "How many halftone styles are there?",
    a: "Twelve. Round, square, diamond, ellipse, line (horizontal and 45°), cross, hex, concentric, wave, FM stochastic and newsprint. Preview them all live before you commit.",
  },
  {
    q: "How do payments work?",
    a: "Secure Stripe checkout — card, Apple Pay, Google Pay or bank. Your plan unlocks the moment Stripe confirms your payment. Monthly plans renew automatically (cancel anytime from your Stripe email receipt). Lifetime plans are a single payment you own forever.",
  },
  {
    q: "Is my artwork private?",
    a: "Halftone processing happens on your device — your art never leaves the browser. AI Vector Creator jobs are sent to the AI provider for that request only, and never stored or trained on.",
  },
  {
    q: "What's the difference between Pro and Creator?",
    a: "Pro ($20/mo) unlocks the Halftone Studio (all 12 styles + underbase). Creator ($45/mo) unlocks everything — Halftone Studio, AI Vector Creator, Watermark Remover, Photo Upgrader, and automatic DTF/DTG optimization on every image.",
  },
  {
    q: "Money-back guarantee?",
    a: "15 days, no forms. Email us and we refund it. That's it.",
  },
];

const STATS = [
  { n: "12", label: "Halftone styles" },
  { n: "< 60s", label: "Average prepress time" },
  { n: "300 DPI", label: "Every export" },
  { n: "4", label: "AI tools built-in" },
];

/* ---------------- Sub-components ---------------- */

function Marquee() {
  return (
    <div className="marquee py-4">
      <div className="marquee-track">
        {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((t, i) => (
          <span key={i} className="mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
            <Flame className="mr-2 inline h-3 w-3 text-[#4aa4ff]" /> {t}
          </span>
        ))}
      </div>
    </div>
  );
}

/* Animated halftone gradient background for hero */
function HalftoneCanvas() {
  const canvasRef = React.useRef(null);
  React.useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    let raf;
    const resize = () => { c.width = c.clientWidth; c.height = c.clientHeight; };
    resize();
    window.addEventListener("resize", resize);
    let t0 = performance.now();
    const draw = () => {
      const w = c.width, h = c.height;
      ctx.clearRect(0, 0, w, h);
      const t = (performance.now() - t0) / 1000;
      const spacing = 22;
      for (let y = 10; y < h; y += spacing) {
        for (let x = 10; x < w; x += spacing) {
          // Density modulated by a slow-moving wave
          const nx = x / w, ny = y / h;
          const wave = 0.5 + 0.5 * Math.sin(nx * 6 + t * 0.6) * Math.cos(ny * 6 - t * 0.3);
          const dens = Math.pow(1 - Math.min(1, Math.abs((nx - 0.5) * 1.4) + Math.abs((ny - 0.5) * 1.4)), 1.8) * wave;
          const r = dens * 6.5;
          if (r < 0.6) continue;
          const alpha = Math.min(0.55, 0.15 + dens * 0.55);
          // Blend blue → gold across the canvas
          const hue = 210 + nx * 40; // 210 blue → 250-ish
          ctx.fillStyle = `hsla(${hue}, 95%, 60%, ${alpha})`;
          ctx.beginPath();
          ctx.arc(x, y, r, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" aria-hidden />;
}

function Hero() {
  const navigate = useNavigate();
  return (
    <section className="relative overflow-hidden">
      <div className="ember-glow absolute inset-0" />
      <div className="absolute inset-0 opacity-70"><HalftoneCanvas /></div>
      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-4 py-24 sm:px-6 lg:grid-cols-[1.15fr_1fr] lg:py-32">
        <div>
          <span className="chip"><span className="h-1.5 w-1.5 rounded-full bg-[#4aa4ff]" /> New · Watermark Remover + Photo Upgrader</span>
          <h1 className="display-font mt-6 text-5xl font-bold leading-[1.02] tracking-tight sm:text-6xl lg:text-[80px]">
            <span className="hero-headline">Softer DTF prints.</span>
            <br />
            <span className="ember-text">Zero Photoshop.</span>
          </h1>
          <p className="mt-6 max-w-xl text-lg text-muted-foreground">
            Upload your art. Get a press-ready halftone separation, a smart white underbase, an AI-cleaned vector — all in under a minute, right in your browser. 12 halftone styles. Softer hand. Less ink. Prints your customers reorder.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button size="lg" className="btn-ember h-12 px-6 font-semibold" onClick={() => navigate("/signup")}>
              Start free — 3 exports/month <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button size="lg" variant="outline" className="h-12 border-border/70 bg-transparent hover:bg-secondary/60" onClick={() => navigate("/pricing")}>
              See Lifetime plans
            </Button>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5 tick" /> No credit card</span>
            <span className="inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5 tick" /> Art never leaves your device</span>
            <span className="inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5 tick" /> 300 DPI · RIP-ready</span>
          </div>

          {/* Mini trust bar */}
          <div className="mt-10 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5"><Star className="h-3.5 w-3.5 fill-[#f5c845] text-[#f5c845]" /> 4.9/5 in our beta group</div>
            <div className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-[#4aa4ff]" /> 15-day money-back</div>
            <div className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-[#f5c845]" /> Prepress in under a minute</div>
          </div>
        </div>

        <div className="relative">
          <div className="absolute -inset-6 rounded-3xl bg-gradient-to-br from-blue-500/10 via-transparent to-amber-400/10 blur-2xl" />
          <TryItWidget />
        </div>
      </div>

      <div className="border-y border-border/70 bg-background/40">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Marquee />
        </div>
      </div>
    </section>
  );
}

function StatsBar() {
  return (
    <section className="relative border-b border-border/60 bg-background/60">
      <div className="mx-auto grid max-w-7xl grid-cols-2 gap-6 px-4 py-12 sm:px-6 lg:grid-cols-4">
        {STATS.map((s) => (
          <div key={s.label} className="rounded-2xl border border-border bg-card p-6">
            <div className="display-font text-4xl font-bold chrome-text">{s.n}</div>
            <div className="mt-1 text-sm text-muted-foreground">{s.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ToolsSuite() {
  const tools = [
    { icon: Layers, name: "Halftone Studio", to: "/studio/halftone", tag: "12 styles", body: "AM, FM, hex, waves, lines and more. Smart underbase + fabric picker. 300 DPI PNG." },
    { icon: Wand2, name: "AI Vector Creator", to: "/studio/vector", tag: "Prompt or upload", body: "Gemini Nano Banana + gpt-image-1. Clean vector-style graphics with transparent background." },
    { icon: Eraser, name: "Watermark Remover", to: "/studio/watermark", tag: "1-click", body: "Wipe stock overlays, logos, timestamps and URLs. Reconstructs the background naturally." },
    { icon: Camera, name: "Photo Upgrader", to: "/studio/upgrade", tag: "Evoto-style", body: "Award-winning photographer retouch. Portrait, product, landscape, wedding, restore and more." },
  ];
  return (
    <section className="relative border-y border-border/60 bg-background py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mx-auto max-w-3xl text-center">
          <span className="chip mx-auto"><Sparkles className="h-3 w-3 text-[#4aa4ff]" /> One workspace, four tools</span>
          <h2 className="display-font mt-4 text-4xl font-bold tracking-tight sm:text-5xl">
            Every prepress job, <span className="ember-text">handled</span>.
          </h2>
          <p className="mt-4 text-muted-foreground">
            Halftones. AI vector cleanup. Watermark removal. World-class photo retouch. All in one browser tab — no plugins, no installs.
          </p>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {tools.map((t) => (
            <Link key={t.name} to={t.to} className="card-hover group relative overflow-hidden rounded-2xl border border-border bg-card p-6">
              <div className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-secondary text-[#4aa4ff] group-hover:bg-[#4aa4ff]/15">
                <t.icon className="h-5 w-5" />
              </div>
              <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{t.tag}</div>
              <h3 className="display-font mt-1 text-lg font-bold">{t.name}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{t.body}</p>
              <div className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-[#4aa4ff]">
                Open tool <ArrowRight className="h-3.5 w-3.5" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyItMatters() {
  return (
    <section id="why" className="relative py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mx-auto max-w-3xl text-center">
          <span className="chip mx-auto"><Flame className="h-3 w-3 text-[#4aa4ff]" /> Why halftones matter</span>
          <h2 className="display-font mt-4 text-4xl font-bold tracking-tight sm:text-5xl">
            That plastic DTF feel is <span className="ember-text">killing reorders</span>.
          </h2>
          <p className="mt-4 text-muted-foreground">
            Solid-ink DTF prints sit on the shirt like a sticker. Heavy, boxy, stiff. Furnace Design Lab breaks solid fills into a controlled dot pattern so less ink lands on the garment and the print moves with the fabric. Softer hand, cleaner press-ready artwork, better reorders.
          </p>
        </div>

        <div className="mt-14 grid gap-6 lg:grid-cols-2">
          <div className="card-hover rounded-2xl border border-border bg-card p-8">
            <div className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground"><X className="h-4 w-4 text-red-400" /> Solid DTF Fill</div>
            <h3 className="display-font text-2xl font-bold">The old way</h3>
            <ul className="mt-4 space-y-3 text-sm text-muted-foreground">
              <li className="flex items-center gap-2"><X className="h-4 w-4 text-red-400" /> Heavy ink coverage</li>
              <li className="flex items-center gap-2"><X className="h-4 w-4 text-red-400" /> Plastic, boxy feel</li>
              <li className="flex items-center gap-2"><X className="h-4 w-4 text-red-400" /> Blocks the fabric</li>
              <li className="flex items-center gap-2"><X className="h-4 w-4 text-red-400" /> Higher cracking risk</li>
              <li className="flex items-center gap-2"><X className="h-4 w-4 text-red-400" /> Customer complaints</li>
            </ul>
          </div>
          <div className="card-hover rounded-2xl chrome-border p-8 shadow-lg shadow-blue-950/30">
            <div className="mb-4 inline-flex items-center gap-2 text-sm text-[#4aa4ff]"><Flame className="h-4 w-4" /> Furnace Design Lab</div>
            <h3 className="display-font text-2xl font-bold">The new standard</h3>
            <ul className="mt-4 space-y-3 text-sm">
              <li className="flex items-center gap-2"><Check className="h-4 w-4 tick" /> Controlled dot pattern</li>
              <li className="flex items-center gap-2"><Check className="h-4 w-4 tick" /> Softer hand feel</li>
              <li className="flex items-center gap-2"><Check className="h-4 w-4 tick" /> More breathable print</li>
              <li className="flex items-center gap-2"><Check className="h-4 w-4 tick" /> Less ink coverage</li>
              <li className="flex items-center gap-2"><Check className="h-4 w-4 tick" /> Better reorder potential</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { icon: Upload, title: "Upload your art", body: "Drop in a PNG, JPG or SVG. Set your print size — we resample to 300 DPI automatically." },
    { icon: ShirtIcon, title: "Choose fabric + style", body: "Dark, light or color. Pick one of 12 halftone styles. Smart Picker sets the LPI, angle and underbase." },
    { icon: Download, title: "Download press-ready", body: "Color layer + smart underbase, both 300 DPI PNG. Import straight into your RIP." },
  ];
  return (
    <section id="how-it-works" className="relative border-y border-border/60 bg-gradient-to-b from-transparent via-secondary/20 to-transparent py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mx-auto max-w-3xl text-center">
          <span className="chip mx-auto"><Zap className="h-3 w-3 text-[#4aa4ff]" /> Press-ready in under a minute</span>
          <h2 className="display-font mt-4 text-4xl font-bold tracking-tight sm:text-5xl">Three steps. Zero guesswork.</h2>
        </div>

        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {steps.map((s, i) => (
            <div key={s.title} className="card-hover relative overflow-hidden rounded-2xl border border-border bg-card p-8">
              <span className="mono absolute right-6 top-6 text-xs text-muted-foreground">0{i + 1}</span>
              <div className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-secondary text-[#4aa4ff]">
                <s.icon className="h-5 w-5" />
              </div>
              <h3 className="display-font text-xl font-bold">{s.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ShowcaseGallery() {
  const shots = [
    { src: "https://images.unsplash.com/photo-1604508230015-5a54faf1fa56", label: "Flat lay · bold graphic" },
    { src: "https://images.unsplash.com/photo-1678872844677-d650b788709b", label: "Halftone on model" },
    { src: "https://images.unsplash.com/photo-1716951988375-37d5793385d0", label: "Reorder stacks" },
    { src: "https://images.unsplash.com/photo-1602810316498-ab67cf68c8e1", label: "Softer hand feel" },
  ];
  return (
    <section className="relative py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mx-auto max-w-3xl text-center">
          <span className="chip mx-auto"><Award className="h-3 w-3 text-[#f5c845]" /> Real shops. Real prints.</span>
          <h2 className="display-font mt-4 text-4xl font-bold tracking-tight sm:text-5xl">Prints your customers <span className="ember-text">actually feel</span>.</h2>
        </div>
        <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {shots.map((s) => (
            <div key={s.src} className="group relative overflow-hidden rounded-2xl border border-border bg-card">
              <img src={s.src} alt={s.label} className="h-72 w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                <span className="mono text-[10px] uppercase tracking-widest text-white/85">{s.label}</span>
                <ArrowRight className="h-3.5 w-3.5 text-white/85 transition-transform group-hover:translate-x-1" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LiveCompare() {
  return (
    <section className="relative py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <span className="chip"><Palette className="h-3 w-3 text-[#4aa4ff]" /> See the output before you print</span>
            <h2 className="display-font mt-4 text-4xl font-bold tracking-tight sm:text-5xl">
              Drag to preview the halftone.
            </h2>
            <p className="mt-4 text-muted-foreground">
              Compare the original art with the AM halftone separation your browser just generated. Zero installs, no plugin, no waiting.
            </p>
            <div className="mt-6 grid gap-3 sm:grid-cols-3">
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="mono text-[11px] uppercase text-muted-foreground">AM Screen</div>
                <div className="mt-1 display-font text-lg font-bold">30 LPI · 22.5°</div>
              </div>
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="mono text-[11px] uppercase text-muted-foreground">Underbase</div>
                <div className="mt-1 display-font text-lg font-bold">Smart Choke</div>
              </div>
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="mono text-[11px] uppercase text-muted-foreground">Output</div>
                <div className="mt-1 display-font text-lg font-bold">300 DPI PNG</div>
              </div>
            </div>
            <div className="mt-8">
              <Link to="/studio/halftone">
                <Button size="lg" className="btn-ember h-12 px-6 font-semibold">
                  Run your own file free <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
          <div>
            <CompareSlider
              leftSrc="https://images.pexels.com/photos/13312402/pexels-photo-13312402.jpeg"
              rightSrc="https://images.unsplash.com/photo-1561714813-4f3b8e4c7f77"
              leftLabel="Original"
              rightLabel="Halftone"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function StylesGrid() {
  return (
    <section className="relative py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mx-auto max-w-3xl text-center">
          <span className="chip mx-auto"><Layers className="h-3 w-3 text-[#4aa4ff]" /> 12 halftone styles</span>
          <h2 className="display-font mt-4 text-4xl font-bold tracking-tight sm:text-5xl">
            Every screen shape you actually print.
          </h2>
          <p className="mt-4 text-muted-foreground">
            Pick the pattern that matches your look — classic AM, stochastic FM, hex, lines, waves, and beyond. Preview live before you export.
          </p>
        </div>

        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {HALFTONE_STYLES_PREVIEW.map((s) => (
            <div key={s.name} className="card-hover group relative overflow-hidden rounded-2xl border border-border bg-card p-5">
              <StylePatternPreview name={s.name} />
              <div className="mt-4 flex items-center justify-between">
                <div className="display-font text-base font-semibold">{s.name}</div>
                <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{s.angle}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* SVG previews for each style */
function StylePatternPreview({ name }) {
  const size = 220;
  const commonProps = { width: "100%", viewBox: `0 0 ${size} ${size}`, className: "rounded-xl bg-[#08101c]" };
  const dot = (cx, cy, r, key) => <circle key={key} cx={cx} cy={cy} r={r} fill="#4aa4ff" />;
  const rect = (x, y, w, h, key) => <rect key={key} x={x} y={y} width={w} height={h} fill="#4aa4ff" />;
  const step = 18;
  const cells = [];
  for (let y = step; y < size; y += step) {
    for (let x = step; x < size; x += step) {
      const dens = 1 - (x + y) / (size * 2);
      cells.push({ x, y, d: Math.max(0.15, dens) });
    }
  }
  const render = () => {
    switch (name) {
      case "Square":
        return cells.map((c, i) => rect(c.x - c.d * 8, c.y - c.d * 8, c.d * 16, c.d * 16, i));
      case "Diamond":
        return cells.map((c, i) => (
          <polygon key={i} points={`${c.x},${c.y - c.d * 8} ${c.x + c.d * 8},${c.y} ${c.x},${c.y + c.d * 8} ${c.x - c.d * 8},${c.y}`} fill="#4aa4ff" />
        ));
      case "Ellipse":
        return cells.map((c, i) => <ellipse key={i} cx={c.x} cy={c.y} rx={c.d * 8} ry={c.d * 5} fill="#4aa4ff" />);
      case "Cross":
        return cells.map((c, i) => (
          <g key={i}>
            <rect x={c.x - c.d * 8} y={c.y - c.d * 2} width={c.d * 16} height={c.d * 4} fill="#4aa4ff" />
            <rect x={c.x - c.d * 2} y={c.y - c.d * 8} width={c.d * 4} height={c.d * 16} fill="#4aa4ff" />
          </g>
        ));
      case "Hex":
        return cells.map((c, i) => {
          const off = Math.floor(c.y / step) % 2 === 0 ? 0 : step / 2;
          const cx = c.x + off, cy = c.y, r = c.d * 8;
          const pts = [0, 1, 2, 3, 4, 5].map((k) => {
            const a = (Math.PI / 3) * k + Math.PI / 6;
            return `${cx + Math.cos(a) * r},${cy + Math.sin(a) * r}`;
          }).join(" ");
          return <polygon key={i} points={pts} fill="#4aa4ff" />;
        });
      case "Concentric":
        return cells.map((c, i) => (
          <g key={i}>
            {[1, 2, 3].map((k) => c.d > k / 4 ? (
              <circle key={k} cx={c.x} cy={c.y} r={k * 3} fill="none" stroke="#4aa4ff" strokeWidth="1" />
            ) : null)}
          </g>
        ));
      case "Line H": {
        const lines = [];
        for (let y = 12; y < size; y += 14) {
          lines.push(<rect key={y} x={0} y={y} width={size} height={Math.max(2, (y / size) * 8)} fill="#4aa4ff" opacity={0.85} />);
        }
        return lines;
      }
      case "Line 45°": {
        const lines = [];
        for (let i = -size; i < size * 2; i += 14) {
          lines.push(<line key={i} x1={i} y1={0} x2={i + size} y2={size} stroke="#4aa4ff" strokeWidth={2 + (i % 8) / 3} />);
        }
        return lines;
      }
      case "Wave": {
        const lines = [];
        for (let y = 20; y < size; y += 18) {
          let d = `M 0 ${y}`;
          for (let x = 0; x <= size; x += 8) {
            d += ` L ${x} ${y + Math.sin(x / 12 + y / 20) * 6}`;
          }
          lines.push(<path key={y} d={d} fill="none" stroke="#4aa4ff" strokeWidth={Math.max(1, (y / size) * 4)} />);
        }
        return lines;
      }
      case "FM Stochastic": {
        const dots = [];
        for (let i = 0; i < 260; i++) {
          const x = Math.random() * size, y = Math.random() * size;
          dots.push(dot(x, y, 1 + Math.random() * 1.2, i));
        }
        return dots;
      }
      case "Newsprint":
        return cells.map((c, i) => dot(c.x, c.y, c.d * 10, i));
      case "AM Round":
      default:
        return cells.map((c, i) => dot(c.x, c.y, c.d * 7, i));
    }
  };
  return (
    <div className="relative overflow-hidden rounded-xl">
      <svg {...commonProps}>{render()}</svg>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#05070d]/95 to-transparent" />
    </div>
  );
}

function VsSection() {
  return (
    <section id="comparison" className="relative py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mx-auto max-w-3xl text-center">
          <span className="chip mx-auto"><Shield className="h-3 w-3 text-[#4aa4ff]" /> Furnace vs the hard way</span>
          <h2 className="display-font mt-4 text-4xl font-bold tracking-tight sm:text-5xl">Faster than Photoshop. Cheaper than a RIP plugin.</h2>
        </div>
        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {[
            { name: "Manual Photoshop", items: ["Requires halftone knowledge", "Easy to leave too much ink", "Underbase built by hand", "30+ minutes per file", "Depends on operator"], negative: true },
            { name: "Desktop RIP plugin", items: ["Expensive per seat", "Locked to a workstation", "Steeper learning curve", "Overkill for most shops", "Still needs operator judgment"], negative: true },
            { name: "Furnace Design Lab", items: ["Choose fabric + style", "Smart Picker handles setup", "Auto-generates underbase", "12 halftone patterns", "Browser-based · Lifetime access"], featured: true },
          ].map((col) => (
            <div key={col.name} className={`card-hover rounded-2xl border p-8 ${col.featured ? "chrome-border shadow-lg shadow-blue-950/40" : "border-border bg-card"}`}>
              {col.featured && <div className="mb-3 inline-flex items-center gap-1 rounded-full border border-[#4aa4ff]/40 bg-[#4aa4ff]/10 px-2 py-1 text-[10px] uppercase tracking-widest text-[#7fc2ff]"><Star className="h-3 w-3" /> Recommended</div>}
              <h3 className="display-font text-xl font-bold">{col.name}</h3>
              <ul className="mt-4 space-y-3 text-sm">
                {col.items.map((it) => (
                  <li key={it} className="flex items-start gap-2 text-muted-foreground">
                    {col.negative ? <X className="mt-0.5 h-4 w-4 text-red-400" /> : <Check className="mt-0.5 h-4 w-4 tick" />}
                    <span className={col.negative ? "" : "text-foreground/90"}>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AiCallout() {
  return (
    <section className="relative py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-[#08101c] via-[#05070d] to-[#05070d] p-10 lg:p-16">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <span className="chip"><Wand2 className="h-3 w-3 text-[#4aa4ff]" /> New · AI Vector Creator</span>
              <h2 className="display-font mt-4 text-4xl font-bold tracking-tight sm:text-5xl">
                From <span className="ember-text">rough idea</span> to <span className="ember-text">print-ready graphic</span>.
              </h2>
              <p className="mt-4 max-w-lg text-muted-foreground">
                Type a prompt or upload any image. Our AI (Gemini Nano Banana + gpt-image-1) rebuilds it as a clean vector-style graphic with transparent background, sharp edges and punched-up color. Creator + All-Access users get automatic DTF/DTG optimization on every image.
              </p>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {[
                  { icon: Sparkles, text: "Generate from a text prompt" },
                  { icon: Upload, text: "Upload → clean + background-remove" },
                  { icon: Wand2, text: "Auto DTF/DTG optimize (Creator)" },
                  { icon: Cpu, text: "Print-ready 300 DPI PNG" },
                ].map((f) => (
                  <div key={f.text} className="flex items-center gap-3 rounded-xl border border-border bg-card/60 p-3 text-sm">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-secondary text-[#4aa4ff]"><f.icon className="h-4 w-4" /></span>
                    {f.text}
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <Link to="/studio/vector">
                  <Button size="lg" className="btn-ember h-12 px-6 font-semibold">Try the AI Vector Creator <ArrowRight className="ml-2 h-4 w-4" /></Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 rounded-3xl bg-gradient-to-tr from-blue-500/10 to-transparent blur-2xl" />
              <div className="relative rounded-2xl border chrome-border p-3">
                <img src="https://images.unsplash.com/photo-1604508230015-5a54faf1fa56" alt="graphic tee" className="h-[420px] w-full rounded-xl object-cover" />
                <div className="absolute bottom-6 left-6 right-6 rounded-xl border border-white/10 bg-black/70 p-4 backdrop-blur">
                  <div className="mono text-[10px] uppercase tracking-widest text-white/60">prompt</div>
                  <div className="mt-1 text-sm text-white">&ldquo;vintage racing tiger with checkered flag, screen-print style&rdquo;</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="relative py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mx-auto max-w-3xl text-center">
          <span className="chip mx-auto"><Quote className="h-3 w-3 text-[#4aa4ff]" /> Shops that stopped printing plastic</span>
          <h2 className="display-font mt-4 text-4xl font-bold tracking-tight sm:text-5xl">What operators say after switching.</h2>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {TESTIMONIALS.map((t) => (
            <div key={t.name} className="card-hover rounded-2xl border border-border bg-card p-6">
              <div className="mb-4 flex text-[#f5c845]">
                {[0,1,2,3,4].map((i) => <Star key={i} className="h-4 w-4 fill-[#f5c845]" />)}
              </div>
              <p className="text-sm leading-relaxed text-muted-foreground">
                &ldquo;{t.quote}&rdquo;
              </p>
              <div className="mt-6 flex items-center gap-3">
                <img src={t.img} alt={t.name} className="h-11 w-11 rounded-full border border-border object-cover" />
                <div>
                  <div className="text-sm font-semibold">{t.name}</div>
                  <div className="mono text-[11px] uppercase tracking-widest text-muted-foreground">{t.location}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingPreview() {
  return (
    <section className="relative py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mx-auto max-w-3xl text-center">
          <span className="chip mx-auto"><Flame className="h-3 w-3 text-[#4aa4ff]" /> Simple, honest pricing</span>
          <h2 className="display-font mt-4 text-4xl font-bold tracking-tight sm:text-5xl">Monthly or Lifetime. <span className="ember-text">Your call</span>.</h2>
          <p className="mt-3 text-muted-foreground">Secure Stripe checkout. Card, Apple Pay, Google Pay. Instant activation. 15-day money-back.</p>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[
            { name: "Free", price: "$0", tag: "Try it out", bullets: ["3 press-ready exports / month", "3 halftone styles", "1 AI tool try / month"], cta: "Start free", to: "/signup" },
            { name: "Pro Monthly", price: "$20", tag: "Solo shops · 30 days", bullets: ["Unlimited Halftone exports", "All 12 halftone styles", "5 AI tool uses / month", "Priority support"], cta: "Get Pro — $20/mo", to: "/purchase?plan=pro-monthly" },
            { name: "Creator Monthly", price: "$45", tag: "Most popular · 30 days", bullets: ["Everything in Pro", "Unlimited AI Vector, Watermark, Photo Upgrade", "Auto DTF/DTG optimize", "Priority artwork help"], cta: "Get Creator — $45/mo", to: "/purchase?plan=creator-monthly", highlight: true },
            { name: "All-Access Lifetime", price: "$100", tag: "Best value · one-time", bullets: ["Everything in Creator", "Every future tool free", "Lifetime updates", "Lifetime priority support"], cta: "Get Lifetime — $100", to: "/purchase?plan=all-access-lifetime" },
          ].map((p) => (
            <div key={p.name} className={`relative rounded-2xl border p-8 ${p.highlight ? "chrome-border shadow-lg shadow-blue-950/40" : "border-border bg-card"}`}>
              {p.highlight && <div className="absolute -top-3 left-6 rounded-full bg-[#4aa4ff] px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-[#05070d]">Most popular</div>}
              <div className="mono text-[11px] uppercase tracking-widest text-muted-foreground">{p.tag}</div>
              <h3 className="display-font mt-2 text-xl font-bold">{p.name}</h3>
              <div className="mt-4 flex items-end gap-1">
                <span className="display-font text-5xl font-bold">{p.price}</span>
              </div>
              <ul className="mt-5 space-y-2 text-sm">
                {p.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 tick" /> <span className="text-foreground/90">{b}</span></li>
                ))}
              </ul>
              <Link to={p.to}>
                <Button className={`mt-7 h-11 w-full font-semibold ${p.highlight ? "btn-ember" : ""}`} variant={p.highlight ? "default" : "outline"}>{p.cta}</Button>
              </Link>
            </div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Link to="/pricing" className="text-sm text-[#4aa4ff] hover:underline">See full pricing + Halftone Lifetime ($50) → </Link>
        </div>
      </div>
    </section>
  );
}

function CtaBanner() {
  const navigate = useNavigate();
  return (
    <section className="relative overflow-hidden py-20">
      <div className="ember-glow absolute inset-0" />
      <div className="mx-auto max-w-4xl px-4 text-center sm:px-6">
        <h2 className="display-font text-4xl font-bold tracking-tight sm:text-5xl">
          Never send another <span className="ember-text">plastic-feeling</span> DTF print.
        </h2>
        <p className="mt-4 text-muted-foreground">Create softer halftones your customers can feel, wear, and reorder. Your first 3 exports this month are free.</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Button size="lg" className="btn-ember h-12 px-6 font-semibold" onClick={() => navigate("/signup")}>Start free — 3 exports</Button>
          <Button size="lg" variant="outline" className="h-12" onClick={() => navigate("/pricing")}>Get Lifetime — from $50</Button>
        </div>
      </div>
    </section>
  );
}

function Faq() {
  return (
    <section id="faq" className="relative py-24">
      <div className="mx-auto max-w-4xl px-4 sm:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <span className="chip mx-auto"><Cpu className="h-3 w-3 text-[#4aa4ff]" /> FAQ</span>
          <h2 className="display-font mt-4 text-4xl font-bold tracking-tight sm:text-5xl">Questions print shops ask.</h2>
        </div>
        <Accordion type="single" collapsible className="mt-10">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-border">
              <AccordionTrigger className="text-left text-base hover:text-[#4aa4ff]">{f.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

export default function LandingPage() {
  return (
    <div className="grain">
      <Navbar />
      <Hero />
      <StatsBar />
      <ToolsSuite />
      <WhyItMatters />
      <HowItWorks />
      <ShowcaseGallery />
      <LiveCompare />
      <StylesGrid />
      <AiCallout />
      <VsSection />
      <Testimonials />
      <PricingPreview />
      <Faq />
      <CtaBanner />
      <Footer />
    </div>
  );
}
